import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setStoryAutoRefresh } from "../services/storyAutoRefresh/storyAutoRefreshApi";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type { StoryMetaApi } from "../services/dataStories/storiesApi.types";
import { useStoryMeta } from "./useStoryMeta";

interface UseStoryAutoRefreshResult {
  enabled: boolean;
  isLoading: boolean;
  toggle: (next: boolean) => Promise<void>;
}

/**
 * Loads and toggles a story's auto-refresh flag. The read is the story row
 * (`useStoryMeta`), the same cache entry StoryLayout loads, so the header adds
 * no request of its own. Optimistic on toggle — the cached flag flips
 * immediately and rolls back only if the write fails, since the live-data
 * behaviour behind `enabled` is entirely a backend concern.
 */
export const useStoryAutoRefresh = (
  workspaceId: string,
  storyId: string,
  onError?: (message: string) => void,
): UseStoryAutoRefreshResult => {
  const queryClient = useQueryClient();
  const { storyMeta, isLoading, errorMessage } = useStoryMeta(workspaceId, storyId);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (errorMessage) onErrorRef.current?.(errorMessage);
  }, [errorMessage]);

  const toggle = useCallback(
    async (next: boolean) => {
      if (!workspaceId || !storyId || !storyMeta) return;

      const metaKey = queryKeys.storyMeta.detail(workspaceId, storyId);
      const setFlag = (value: boolean) =>
        queryClient.setQueryData<StoryMetaApi>(metaKey, (current) =>
          current ? { ...current, is_auto_refresh: value } : current,
        );

      setFlag(next);
      try {
        const saved = await setStoryAutoRefresh(workspaceId, storyId, next);
        setFlag(saved.enabled);
      } catch (error) {
        setFlag(!next);
        onErrorRef.current?.(
          getApiErrorMessage(error, "Unable to update auto refresh."),
        );
      }
    },
    [queryClient, workspaceId, storyId, storyMeta],
  );

  return { enabled: storyMeta?.is_auto_refresh ?? false, isLoading, toggle };
};
